import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Select, Store } from '@ngxs/store';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription, firstValueFrom } from 'rxjs';

import { AIMarAccountLinkSuggestion, AIService } from '../../ai/ai.service';
import { OverheadCategory } from '../../models/OverheadCategory';
import { YukiMarGlAccount } from '../../models/yuki/YukiMarGlAccount';
import { GetAllOverheadCategories } from '../../yuki/yuki.actions';
import { YukiService } from '../../yuki/yuki.service';
import { YukiState } from '../../yuki/yuki.state';

type MarAiPreviewLink = {
    overheadCategoryId: string;
    overheadCategoryName: string;
    accountNumber: string;
    accountName: string;
    confidence: number | null;
    reason: string | null;
};

@Component({
    selector: 'app-yuki-mar-linking-modal',
    templateUrl: './yuki-mar-linking-modal.component.html',
    styleUrls: ['./yuki-mar-linking-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class YukiMarLinkingModalComponent implements OnInit, OnDestroy {
    @Select(YukiState.overheadCategories) overheadCategories$: Observable<OverheadCategory[]>;

    loadingOverheadCategories = true;
    loadingMarAccounts = true;
    overheadCategories: OverheadCategory[] = [];
    marAccounts: YukiMarGlAccount[] = [];
    showLinkedOnly = false;
    accountSearchTerm = '';
    aiLinkingLoading = false;
    aiApplying = false;
    aiProposals: MarAiPreviewLink[] = [];
    savingByAccountNumber: Record<string, boolean> = {};

    private subscriptions = new Subscription();

    constructor(
        public activeModal: NgbActiveModal,
        private store: Store,
        private yukiService: YukiService,
        private toastr: ToastrService,
        private aiService: AIService,
    ) {}

    ngOnInit(): void {
        console.log('[MAR-LINKING][Modal] init');
        this.subscriptions.add(
            this.overheadCategories$.subscribe((categories: unknown) => {
                console.log('[MAR-LINKING][Modal] overhead categories raw payload', categories);
                const safeList = this.extractCategoryList(categories);
                this.overheadCategories = safeList
                    .map((category: any) => ({
                        ...category,
                        id: this.normalizeIdentifier(category?.id || category?.uuid),
                    }))
                    .sort((a: any, b: any) => (a?.categoryName || a?.displayName || '').localeCompare(b?.categoryName || b?.displayName || ''));
                console.log('[MAR-LINKING][Modal] overhead categories normalized', this.overheadCategories);
                this.loadingOverheadCategories = false;
            }),
        );

        this.subscriptions.add(this.store.dispatch(new GetAllOverheadCategories()).subscribe());
        this.fetchMarAccounts();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    get loading(): boolean {
        return this.loadingMarAccounts;
    }

    get filteredMarAccounts(): YukiMarGlAccount[] {
        const search = (this.accountSearchTerm || '').toString().trim().toLowerCase();

        let accounts = this.marAccounts;

        if (this.showLinkedOnly) {
            accounts = accounts.filter((account) => !!this.normalizeIdentifier(account?.overheadCategoryId));
        }

        if (search.length > 0) {
            accounts = accounts.filter((account) => {
                const accountNumber = (account?.accountNumber || '').toString().toLowerCase();
                const accountName = (account?.accountName || '').toString().toLowerCase();
                return accountNumber.includes(search) || accountName.includes(search);
            });
        }

        return accounts;
    }

    get unlinkedOverheadCategories(): OverheadCategory[] {
        if (!this.overheadCategories || this.overheadCategories.length === 0) {
            return [];
        }

        const linkedCategoryIds = new Set(
            (this.marAccounts || [])
                .map((account) => this.normalizeIdentifier(account?.overheadCategoryId))
                .filter((categoryId): categoryId is string => !!categoryId),
        );

        return this.overheadCategories.filter((category: any) => {
            const categoryId = this.getCategoryId(category);
            return !categoryId || !linkedCategoryIds.has(categoryId);
        });
    }

    get unlinkedMarAccounts(): YukiMarGlAccount[] {
        return (this.marAccounts || []).filter((account) => !this.normalizeIdentifier(account?.overheadCategoryId));
    }

    toggleShowLinkedOnly(): void {
        this.showLinkedOnly = !this.showLinkedOnly;
    }

    async generateAiLinkProposals(): Promise<void> {
        if (this.aiLinkingLoading) {
            return;
        }

        const categories = this.unlinkedOverheadCategories || [];
        const accounts = this.unlinkedMarAccounts || [];

        if (categories.length === 0) {
            this.toastr.info('Er zijn geen overheadkosten meer die gelinkt moeten worden.');
            this.aiProposals = [];
            return;
        }

        if (accounts.length === 0) {
            this.toastr.info('Er zijn geen vrije MAR rekeningen beschikbaar om te koppelen.');
            this.aiProposals = [];
            return;
        }

        this.aiLinkingLoading = true;
        try {
            const suggestions: AIMarAccountLinkSuggestion[] = await this.aiService.suggestMarAccountLinks({
                overheadCategories: categories.map((category: any) => ({
                    id: this.getCategoryId(category),
                    displayName: category?.categoryName || category?.displayName || '',
                })),
                marAccounts: accounts.map((account: any) => ({
                    accountNumber: String(account?.accountNumber || '').trim(),
                    accountName: account?.accountName || '',
                })),
            });

            const categoryById = new Map(categories.map((category: any) => [this.getCategoryId(category), category]));
            const accountByNumber = new Map(accounts.map((account: any) => [String(account?.accountNumber || '').trim(), account]));

            this.aiProposals = suggestions
                .map((suggestion) => {
                    const category = categoryById.get(suggestion.overheadCategoryId);
                    const account = accountByNumber.get(suggestion.accountNumber);
                    if (!category || !account) {
                        return null;
                    }

                    return {
                        overheadCategoryId: suggestion.overheadCategoryId,
                        overheadCategoryName: category?.categoryName || category?.displayName || '-',
                        accountNumber: suggestion.accountNumber,
                        accountName: account?.accountName || '-',
                        confidence: typeof suggestion.confidence === 'number' ? suggestion.confidence : null,
                        reason: suggestion.reason || null,
                    } as MarAiPreviewLink;
                })
                .filter((link): link is MarAiPreviewLink => !!link);

            if (this.aiProposals.length === 0) {
                this.toastr.info('AI kon geen betrouwbare voorstellen maken.');
            } else {
                this.toastr.success(`${this.aiProposals.length} AI-voorstellen klaar om toe te passen.`);
            }
        } catch (error) {
            console.error('[MAR-LINKING][Modal] AI proposal generation failed', error);
            this.aiProposals = [];
            this.toastr.error('AI-koppeling voorstellen ophalen is mislukt.');
        } finally {
            this.aiLinkingLoading = false;
        }
    }

    async applyAiLinkProposals(): Promise<void> {
        if (this.aiApplying || !this.aiProposals || this.aiProposals.length === 0) {
            return;
        }

        this.aiApplying = true;
        let successCount = 0;

        try {
            for (const proposal of this.aiProposals) {
                const account = this.marAccounts.find((item) => this.getAccountKey(item) === proposal.accountNumber);
                if (!account) {
                    continue;
                }

                const accountKey = this.getAccountKey(account);
                this.savingByAccountNumber[accountKey] = true;

                try {
                    const updated = await firstValueFrom(
                        this.yukiService.upsertMarAccountMapping(account.accountNumber, account.accountName, proposal.overheadCategoryId),
                    );

                    account.overheadCategoryId = this.normalizeIdentifier(updated?.overheadCategoryId ?? proposal.overheadCategoryId);
                    successCount++;
                } catch (error) {
                    console.error('[MAR-LINKING][Modal] Failed applying AI proposal', { proposal, error });
                } finally {
                    this.savingByAccountNumber[accountKey] = false;
                }
            }

            this.aiProposals = [];

            if (successCount > 0) {
                this.toastr.success(`${successCount} AI-koppelingen toegepast.`);
            } else {
                this.toastr.warning('Geen AI-koppelingen toegepast.');
            }
        } finally {
            this.aiApplying = false;
        }
    }

    onOverheadCategorySelected(account: YukiMarGlAccount, selectedValue: string | null): void {
        const selectedOverheadCategoryId = selectedValue && selectedValue.length > 0 ? selectedValue : null;
        const accountKey = this.getAccountKey(account);

        console.log('[MAR-LINKING][Modal] selection changed', {
            accountNumber: account?.accountNumber,
            accountName: account?.accountName,
            previousOverheadCategoryId: account?.overheadCategoryId,
            selectedValue,
            selectedOverheadCategoryId,
        });

        this.savingByAccountNumber[accountKey] = true;
        let updateHandled = false;
        this.subscriptions.add(
            this.yukiService.upsertMarAccountMapping(account.accountNumber, account.accountName, selectedOverheadCategoryId).subscribe({
                next: (updated) => {
                    updateHandled = true;
                    console.log('[MAR-LINKING][Modal] save response', {
                        accountNumber: account?.accountNumber,
                        updated,
                    });
                    account.overheadCategoryId = this.normalizeIdentifier(updated?.overheadCategoryId ?? selectedOverheadCategoryId);
                    console.log('[MAR-LINKING][Modal] account after normalize/assign', {
                        accountNumber: account?.accountNumber,
                        newOverheadCategoryId: account?.overheadCategoryId,
                    });
                    this.savingByAccountNumber[accountKey] = false;
                },
                error: (error) => {
                    updateHandled = true;
                    console.error('[MAR-LINKING][Modal] save failed', {
                        accountNumber: account?.accountNumber,
                        selectedOverheadCategoryId,
                        error,
                    });
                    this.savingByAccountNumber[accountKey] = false;
                    this.toastr.error('Ops, koppelen van rekeningnummer mislukt. Probeer opnieuw.');
                },
                complete: () => {
                    console.log('[MAR-LINKING][Modal] save complete', {
                        accountNumber: account?.accountNumber,
                        updateHandled,
                    });
                    this.savingByAccountNumber[accountKey] = false;
                    if (!updateHandled) {
                        this.toastr.warning('Je hebt geen rechten om deze koppeling te wijzigen.');
                    }
                },
            }),
        );
    }

    private extractCategoryList(categories: unknown): OverheadCategory[] {
        if (Array.isArray(categories)) {
            return categories as OverheadCategory[];
        }

        if (!categories || typeof categories !== 'object') {
            return [];
        }

        const payload = categories as any;
        if (Array.isArray(payload.content)) {
            return payload.content as OverheadCategory[];
        }
        if (Array.isArray(payload.data)) {
            return payload.data as OverheadCategory[];
        }
        if (Array.isArray(payload.results)) {
            return payload.results as OverheadCategory[];
        }

        return [];
    }

    private fetchMarAccounts(): void {
        this.loadingMarAccounts = true;
        console.log('[MAR-LINKING][Modal] fetching MAR accounts...');
        let responseHandled = false;
        this.subscriptions.add(
            this.yukiService.getMarAccounts().subscribe({
                next: (accounts: unknown) => {
                    responseHandled = true;
                    console.log('[MAR-LINKING][Modal] MAR accounts raw payload', accounts);
                    const safeAccounts = this.extractMarAccountList(accounts);

                    if (safeAccounts.length === 0 && accounts && typeof accounts === 'object' && !Array.isArray(accounts)) {
                        this.toastr.info('MAR rekeningnummers konden niet gelezen worden uit de response. Probeer te refreshen.');
                    }

                    this.marAccounts = safeAccounts
                        .map((account) => ({
                            ...account,
                            overheadCategoryId: this.normalizeIdentifier(account?.overheadCategoryId),
                        }))
                        .sort((a, b) => (a?.accountNumber || '').localeCompare(b?.accountNumber || ''));
                    console.log('[MAR-LINKING][Modal] MAR accounts normalized (first 25)', this.marAccounts.slice(0, 25));
                    this.loadingMarAccounts = false;
                },
                error: (error) => {
                    responseHandled = true;
                    console.error('[MAR-LINKING][Modal] failed fetching MAR accounts', error);
                    this.marAccounts = [];
                    this.loadingMarAccounts = false;
                    this.toastr.error('Ops, rekeningnummers ophalen uit Yuki is mislukt.');
                },
                complete: () => {
                    console.log('[MAR-LINKING][Modal] fetch MAR accounts complete', { responseHandled, count: this.marAccounts?.length || 0 });
                    this.loadingMarAccounts = false;
                    if (!responseHandled) {
                        this.marAccounts = [];
                        this.toastr.warning('Je hebt geen rechten om MAR-rekeningnummers te bekijken.');
                    }
                },
            }),
        );
    }

    private extractMarAccountList(accounts: unknown): YukiMarGlAccount[] {
        if (Array.isArray(accounts)) {
            return accounts as YukiMarGlAccount[];
        }

        if (!accounts || typeof accounts !== 'object') {
            return [];
        }

        const payload = accounts as any;
        if (Array.isArray(payload.data)) {
            return payload.data as YukiMarGlAccount[];
        }
        if (Array.isArray(payload.content)) {
            return payload.content as YukiMarGlAccount[];
        }
        if (Array.isArray(payload.results)) {
            return payload.results as YukiMarGlAccount[];
        }
        if (Array.isArray(payload.marAccounts)) {
            return payload.marAccounts as YukiMarGlAccount[];
        }
        if (Array.isArray(payload.accounts)) {
            return payload.accounts as YukiMarGlAccount[];
        }
        if (Array.isArray(payload.GlAccount)) {
            return payload.GlAccount as YukiMarGlAccount[];
        }
        if (payload.GlAccount && typeof payload.GlAccount === 'object') {
            return [payload.GlAccount as YukiMarGlAccount];
        }

        const objectValues = Object.values(payload);
        if (objectValues.length > 0 && objectValues.every((entry: any) => this.isMarAccountLike(entry))) {
            return objectValues as YukiMarGlAccount[];
        }

        return [];
    }

    private isMarAccountLike(entry: any): boolean {
        if (!entry || typeof entry !== 'object') return false;
        const accountNumber = entry.accountNumber ?? entry.code;
        const accountName = entry.accountName ?? entry.description;
        return !!accountNumber || !!accountName;
    }

    getAccountKey(account: YukiMarGlAccount): string {
        return (account?.accountNumber || '').toString().trim();
    }

    getCategoryId(category: any): string {
        return this.normalizeIdentifier(category?.id || category?.uuid) || '';
    }

    private normalizeIdentifier(value: unknown): string | null {
        if (value === null || value === undefined) {
            return null;
        }

        const normalized = String(value).trim().toLowerCase();
        return normalized.length > 0 ? normalized : null;
    }
}
